import React, { useEffect, useState } from 'react'
import { Field, Label, Input, Button, RadioGroup, Radio, Select } from '../../../atoms'
import BusinessFinder from './business-finder'
import { getExistingConsent, updateConsents } from '@uswitch/rvuconsent'
import { NumberInput } from './number-input'
import { RegisterOptions } from 'react-hook-form/dist/types'
import { FsCore } from 'types/fs-core'
import { RegisterTrack } from '../sme-form'
import { useComponentTracking } from '../../../utils/component-events'
import ProgressCircle from '../../form/progress-circle'
import { isEmpty } from 'lodash-es'

interface ProfileInformationProps {
  registerWithTrack: (rules: RegisterOptions, detail: string) => RegisterTrack
  register: (fieldName: string) => void
  setValue: (fieldName: string, value: any) => void
  unregister: (fieldName: string) => void
  businessDetails: Record<string, any>
  directorDetails: Record<string, any>
  hideButton?: boolean
  consents: {
    marketing: boolean
  }
  setConsents: React.Dispatch<
    React.SetStateAction<{
      marketing: boolean
    }>
  >
  setValidationError?: React.Dispatch<React.SetStateAction<string | null>>
  setError?: React.Dispatch<React.SetStateAction<string | null>>
  clearErrors?: React.Dispatch<React.SetStateAction<string | null>>
  errors: Record<string, any>
  brand: FsCore.FSBrand
  consentSource: string
  formName: string
  businessOptions?: { name: string; text: string }[]
  companyFiltering?: string[]
  ctaText: string
  landingPage?: string | null
  handleSubmit: (body: Record<string, any>) => void
  control: any
  TypeOfBusinessHelpHTML?: () => JSX.Element
  defaultBusinessNameInput?: string
}

const ProfileInformation: React.FC<ProfileInformationProps> = ({
  registerWithTrack,
  register,
  setValue,
  unregister,
  businessDetails,
  directorDetails,
  hideButton,
  consents,
  setConsents,
  setValidationError = () => {},
  setError = () => {},
  clearErrors = () => {},
  errors,
  brand,
  consentSource,
  formName,
  businessOptions = [
    { name: 'limitedCompany', text: 'Limited Company' },
    { name: 'soleTrader', text: 'Sole Trader' },
    { name: 'other', text: 'Other (e.g. Partnership)' },
  ],
  companyFiltering = ['ltd', 'plc'],
  ctaText,
  landingPage,
  handleSubmit,
  control,
  TypeOfBusinessHelpHTML,
  defaultBusinessNameInput,
}) => {
  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()
  const consentName = 'sme-fusion.soft-opt-in'
  const sendConsents = async (email: string, status: boolean) => {
    return updateConsents(
      {
        email,
        consents: [
          {
            name: consentName,
            status,
            source: consentSource,
            sourceUrl: window.location.href,
          },
        ],
      },
      true,
    )
  }

  const [showConsentCheckbox, setShowConsentCheckbox] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const businessType = businessDetails.businessType
  const isLimitedCompany = businessType === 'limitedCompany'
  const isBusinessNameEmpty = !businessDetails.businessName
  const isLoansLandingPage =
    landingPage === 'business-loans' || landingPage === 'startup-business-loans'
  const isInvalidDirectorEmail =
    !directorDetails.emailAddress || errors?.directorDetails?.emailAddress
  const isAddressEmpty =
    !businessDetails.address?.summaryline ||
    !businessDetails.address?.street ||
    !businessDetails.address?.posttown ||
    !businessDetails.address?.postcode

  const isExistenceTimeEmpty = businessDetails.existenceYears === 'default'
  const isTurnoverEmpty = !businessDetails.turnover
  const isBorrowingAmountEmpty = !businessDetails.borrowingAmount
  const isBorrowingReasonEmpty = !businessDetails.borrowingReason
  const disabledFields =
    isBusinessNameEmpty || isInvalidDirectorEmail || isAddressEmpty || isExistenceTimeEmpty
  const isDisabled = isLoansLandingPage
    ? disabledFields ||
      isTurnoverEmpty ||
      isBorrowingAmountEmpty ||
      isBorrowingReasonEmpty ||
      !isEmpty(errors)
    : disabledFields || !isEmpty(errors)
  const directorEmail = directorDetails.emailAddress
  const [businessNameError, setBusinessNameError] = useState('')

  useEffect(() => {
    let isRequestCurrent = true
    if (directorEmail) {
      getExistingConsent({ email: directorEmail, consentName, consentType: 'any' })
        .then((hasConsent) => {
          if (isRequestCurrent) {
            setShowConsentCheckbox(!hasConsent)
          }
        })
        .catch(() => {
          if (isRequestCurrent) {
            setShowConsentCheckbox(true)
          }
        })
    }
    return () => {
      isRequestCurrent = false
    }
  }, [directorEmail])

  useEffect(() => {
    if (
      !(isInvalidDirectorEmail || (!isLimitedCompany && (isAddressEmpty || isExistenceTimeEmpty)))
    ) {
      setBusinessNameError(isBusinessNameEmpty ? 'Please select your company name' : '')
    }
  }, [businessDetails])

  const submitProfile = async () => {
    try {
      setSubmitLoading(true)
      await sendConsents(directorEmail, consents.marketing).then(() => {
        setShowConsentCheckbox(false)
      })

      const finalBody = {
        businessType,
        companyType: businessDetails.companyType,
        businessName: businessDetails.businessName,
        businessAddress: businessDetails.address,
        email: directorEmail,
        existenceYears: businessDetails.existenceYears,
        ...(businessDetails?.turnover ? { turnover: parseInt(businessDetails.turnover) } : {}),
        ...(businessDetails?.borrowingAmount
          ? { borrowingAmount: parseInt(businessDetails.borrowingAmount) }
          : {}),
        ...(businessDetails?.borrowingReason
          ? { borrowingReason: businessDetails.borrowingReason }
          : {}),
        companyRegistrationNumber: businessDetails.companyNumber,
        date_of_creation: businessDetails.date_of_creation,
        dateOfCreation: businessDetails.dateOfCreation,
      }
      handleSubmit(finalBody)
    } catch (e) {
      setSubmitLoading(false)
      setValidationError('Oops looks like something went wrong. Please try again later')
    }
  }

  return (
    <>
      <Field className='SMEform__typeOfBusiness'>
        <Label>Type of business</Label>
        <div className='SMEform__input'>
          <RadioGroup column={true}>
            {businessOptions.map(({ name, text }, index) => {
              return (
                <Radio
                  name={'businessDetails.businessType'}
                  value={name}
                  currentValue={businessDetails.businessType ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('businessDetails.businessType', e.target.value)
                  }}
                  key={index}
                  ref={registerWithTrack(
                    {
                      required: 'Please enter your type of business',
                    },
                    'businessType',
                  )}
                >
                  {text}
                </Radio>
              )
            })}
          </RadioGroup>
          {TypeOfBusinessHelpHTML && <TypeOfBusinessHelpHTML />}
        </div>
      </Field>

      <BusinessFinder
        businessDetails={businessDetails}
        brand={brand}
        register={register}
        registerWithTrack={registerWithTrack}
        setValue={setValue}
        setError={setError}
        clearErrors={clearErrors}
        errors={errors}
        unregister={unregister}
        soleTrader={businessType === 'soleTrader'}
        companyFilter={companyFiltering}
        businessNameError={businessNameError}
        defaultBusinessNameInput={defaultBusinessNameInput}
      />
      {businessType === 'soleTrader' && (
        <Field className='SMEform__typeOfBusiness'>
          <Label className='required'>How old is your business?</Label>
          <div className='SMEform__input'>
            <Select
              name={'businessDetails.existenceYears'}
              value={businessDetails.existenceYears ?? 'default'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setValue('businessDetails.existenceYears', e.target.value)
              }}
              ref={registerWithTrack(
                {
                  required: 'Please tell us how old your business is',
                },
                'existenceYears',
              )}
              options={[
                { value: 'default', text: 'Please select' },
                { value: '0', text: 'Less than 1 year' },
                { value: '1', text: '1 year' },
                { value: '2', text: '2 years' },
                { value: '3', text: '3 years' },
                { value: '4', text: '4 years' },
                { value: '5+', text: '5+ years' },
              ]}
            />
          </div>
        </Field>
      )}
      {(isLoansLandingPage || landingPage === 'invoicing-financing') && (
        <>
          <Field className='SMEform__typeOfBusiness'>
            <Label className='required'>What is your annual turnover?</Label>
            <div className='SMEform__input'>
              <NumberInput
                name='businessDetails.turnover'
                control={control}
                trackField={(name: string, value: string) =>
                  trackComponent('Form Event', 'formFieldInteracted', {
                    formStep: name,
                    selected: value,
                  })
                }
                rules={{ required: 'Please tell us your annual business turnover' }}
              />
              {errors?.businessDetails?.turnover && (
                <div className='SMEform__error'>{errors?.businessDetails?.turnover.message}</div>
              )}
            </div>
          </Field>
        </>
      )}
      {isLoansLandingPage && (
        <>
          <Field className='SMEform__typeOfBusiness'>
            <Label className='required'>How much are you looking to borrow?</Label>
            <div className='SMEform__input'>
              <NumberInput
                name='businessDetails.borrowingAmount'
                control={control}
                trackField={(name: string, value: string) =>
                  trackComponent('Form Event', 'formFieldInteracted', {
                    formStep: name,
                    selected: value,
                  })
                }
                rules={{ required: 'Please tell us how much you want to borrow' }}
              />
              {errors?.businessDetails?.borrowingAmount && (
                <div className='SMEform__error'>
                  {errors?.businessDetails?.borrowingAmount.message}
                </div>
              )}
            </div>
          </Field>
          <Field className='SMEform__typeOfBusiness'>
            <Label className='required'>What is the finance for?</Label>
            <div className='SMEform__input'>
              <Select
                name={'businessDetails.borrowingReason'}
                value={businessDetails.borrowingReason ?? ''}
                options={[
                  { value: '', text: 'Select' },
                  { value: 'Working Capital', text: 'Working Capital' },
                  { value: 'Asset Purchase', text: 'Asset Purchase' },
                  { value: 'Refinancing a loan', text: 'Refinancing a loan' },
                  { value: 'Property finance', text: 'Property finance' },
                  { value: 'Other', text: 'Other' },
                  { value: "I'm not sure", text: "I'm not sure" },
                ]}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setValue('businessDetails.borrowingReason', e.target.value)
                }}
                ref={registerWithTrack(
                  {
                    required: 'Please tell us what the finance is for',
                  },
                  'borrowingReason',
                )}
              />
              {errors?.businessDetails?.borrowingReason && (
                <div className='SMEform__error'>
                  {errors?.businessDetails?.borrowingReason.message}
                </div>
              )}
            </div>
          </Field>
        </>
      )}
      {!(formName === 'Zempler form' && businessType === 'other') && (
        <Field className='SMEform__emailAddress'>
          <Label className='required'>Email Address</Label>
          <div className='SMEform__input'>
            <input
              name='directorDetails.emailAddress'
              value={directorDetails.emailAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setValue(`directorDetails.emailAddress`, e.target.value.trim())
              }}
              type='text'
              ref={registerWithTrack(
                {
                  required: { value: true, message: 'Please enter your email address' },
                  pattern: {
                    value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                    message: 'Please enter a valid email address',
                  },
                },
                'emailAddress',
              )}
            />
            {showConsentCheckbox && (
              <Label className='SMEform__fscsConsent'>
                <Input
                  type='checkbox'
                  name='otherDetails.marketingConsent'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setConsents({ ...consents, marketing: !e.target.checked })
                  }}
                  ref={registerWithTrack({}, 'marketingConsent')}
                />
                <div>
                  I do not want to receive money-saving deals and expert insights from money.co.uk.
                </div>
              </Label>
            )}
            {errors?.directorDetails?.emailAddress && (
              <div className='SMEform__error'>{errors?.directorDetails?.emailAddress.message}</div>
            )}
            <p style={{ marginTop: '12px' }}>
              We can help you save money on both your business and personal bills including
              financial services, insurance, energy and telecoms.{' '}
              <a href='https://www.money.co.uk/privacy-notice' target='blank'>
                Privacy Notice
              </a>{' '}
            </p>
          </div>
        </Field>
      )}
      {formName === 'Journey form' && (
        <div className='SMEform__input'>
          <div className='SMEform__fscsConsent'>
            <p>
              We will show you a range of products from our partners. View their{' '}
              <a href='https://www.money.co.uk/privacy-notice/partners' target='blank'>
                privacy notices
              </a>{' '}
              for more information.
            </p>
          </div>
        </div>
      )}
      {!hideButton && ctaText && !(formName === 'Zempler form' && businessType === 'other') && (
        <div className='SMEform__sectionButton'>
          {submitLoading && formName === 'Journey form' ? (
            <div className='SMEform__loadingMessage'>
              <div className='SMEform__loadingMessage__spinner'>
                <ProgressCircle height={48} />
              </div>
              <p>
                Thanks for your patience, it should only take a minute or two. Please don&apos;t hit
                refresh or back
              </p>
            </div>
          ) : (
            <Button
              variant='primary'
              size='lg'
              dataClickTracking={{
                component_category: 'component',
                component_name: 'sme-form',
                contentful_variant: '',
                additionalProps: {
                  text: { ctaText },
                },
              }}
              onClick={submitProfile}
              disabled={isDisabled}
            >
              {ctaText}
            </Button>
          )}
        </div>
      )}
    </>
  )
}

export default ProfileInformation
